import { useState, useMemo, useRef, useEffect } from 'react';
import { getGlobalSearchItems, staticSearchItems, getSectionBadgeStyle } from '../utils/searchCatalog.js';

const Breadcrumbs = ({ items = [], onNavigate }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchRef = useRef(null);
  const searchInputRef = useRef(null);

  const searchItems = useMemo(() => {
    if (!isSearchOpen && !searchQuery) {
      return staticSearchItems;
    }
    return getGlobalSearchItems();
  }, [isSearchOpen, searchQuery]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useEffect(() => {
    const handleOpenSearch = () => {
      setIsSearchOpen(true);
      window.setTimeout(() => searchInputRef.current?.focus(), 40);
    };

    window.addEventListener('campusguide:open-search', handleOpenSearch);
    return () => {
      window.removeEventListener('campusguide:open-search', handleOpenSearch);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        setIsSearchOpen(true);
        searchInputRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const query = searchQuery.trim().toLowerCase();
  const filteredSearchItems = useMemo(() => {
    if (!query) {
      return staticSearchItems.slice(0, 8);
    }

    return searchItems
      .filter((item) =>
        [item.label, item.section, item.keywords || ''].some((value) =>
          value.toLowerCase().includes(query),
        ),
      )
      .slice(0, 24);
  }, [query, searchItems]);

  const handleSearchSubmit = () => {
    if (filteredSearchItems.length > 0) {
      const firstMatch = filteredSearchItems[0];
      onNavigate(firstMatch.path);
      setIsSearchOpen(false);
      setSearchQuery('');
    }
  };

  const handleButtonClick = () => {
    if (searchQuery.trim()) {
      handleSearchSubmit();
    } else {
      setIsSearchOpen((prev) => !prev);
      if (!isSearchOpen) {
        window.setTimeout(() => searchInputRef.current?.focus(), 40);
      }
    }
  };

  const handleLinkNavigate = (e, path) => {
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button !== 0) {
      return;
    }
    e.preventDefault();
    onNavigate(path);
  };

  if (!items.length) {
    return null;
  }

  return (
    <div className="w-full relative z-20 flex items-center justify-between gap-4 px-4 py-1.5 sm:px-5 sm:py-2 bg-white/95 border border-slate-200/90 rounded-[20px] shadow-[0_12px_28px_rgba(15,23,42,0.03)] backdrop-blur-xl dark:bg-[#06142b]/95 dark:border-[#1e355f]/90 dark:shadow-[0_12px_28px_rgba(2,8,23,0.4)]">
      <nav aria-label="Breadcrumb" className="min-w-0">
        <ol className="flex flex-wrap items-center gap-1.5 text-xs sm:text-sm font-semibold text-slate-500 dark:text-slate-400">
          {items.map((item, index) => {
            const isLast = index === items.length - 1;

            return (
              <li key={`${item.label}-${index}`} className="inline-flex items-center gap-1.5 min-w-0">
                {index > 0 ? <span className="text-slate-300 dark:text-slate-700 shrink-0" aria-hidden="true">&rsaquo;</span> : null}
                {item.path && !isLast ? (
                  <a
                    href={`#${item.path}`}
                    onClick={(e) => handleLinkNavigate(e, item.path)}
                    className="rounded-lg px-1 py-0.5 transition hover:text-slate-900 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-500/15 dark:hover:text-white truncate"
                  >
                    {item.label}
                  </a>
                ) : (
                  <span
                    aria-current={isLast ? 'page' : undefined}
                    className={isLast ? 'px-1 py-0.5 font-black text-slate-900 dark:text-white truncate' : 'px-1 py-0.5 truncate'}
                  >
                    {item.label}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </nav>

      {/* Desktop Search Bar */}
      <div ref={searchRef} className="relative hidden lg:block transition-all duration-300 ease-out w-72 focus-within:w-96 shrink-0">
        <div className="flex items-center justify-between gap-2 rounded-full border border-slate-200 bg-slate-50/50 px-3 py-1.5 transition duration-200 focus-within:border-blue-400 focus-within:bg-white focus-within:ring-4 focus-within:ring-blue-100/30 dark:border-[#1e355f] dark:bg-[#030b1c]/65 dark:focus-within:border-blue-500 dark:focus-within:bg-[#030b1c]">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <svg
              viewBox="0 0 24 24"
              className="h-3.5 w-3.5 text-slate-400 shrink-0"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              onFocus={() => setIsSearchOpen(true)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleSearchSubmit();
                }
              }}
              placeholder="Search colleges, guides, cutoffs..."
              aria-label="Search colleges, guides, cutoffs"
              className="w-full bg-transparent text-xs text-slate-800 outline-none placeholder:text-slate-400 dark:text-white"
            />
          </div>
          <button
            type="button"
            onMouseDown={(event) => {
              event.preventDefault();
              handleButtonClick();
            }}
            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white transition hover:bg-blue-700 hover:scale-105 active:scale-95 shadow-[0_4px_10px_rgba(37,99,235,0.3)]"
            aria-label="Submit Search"
          >
            <svg
              viewBox="0 0 24 24"
              className="h-3 w-3"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
            >
              <path d="M5 12h14" />
              <path d="m12 5 7 7-7 7" />
            </svg>
          </button>
        </div>

        {isSearchOpen && (
          <div className="absolute right-0 top-[calc(100%+8px)] z-50 w-[24rem] rounded-[24px] border border-slate-200 bg-white p-4 shadow-[0_18px_40px_rgba(15,23,42,0.14)] dark:border-[#1e355f] dark:bg-[#06142b] dark:shadow-[0_18px_40px_rgba(2,8,23,0.55)]">
            <div className="mb-3">
              <p className="text-sm font-black text-slate-950 dark:text-white">Search</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">Search pages, colleges, and institutes</p>
            </div>
            <div className="max-h-[22rem] overflow-y-auto pr-1">
              <div className="space-y-2">
                {filteredSearchItems.map((item) => (
                  <a
                    key={item.path}
                    href={`#${item.path}`}
                    onClick={(e) => {
                      handleLinkNavigate(e, item.path);
                      setIsSearchOpen(false);
                      setSearchQuery('');
                    }}
                    className="flex w-full items-center justify-between gap-3 rounded-2xl border border-slate-100 bg-white px-4 py-3 text-left transition duration-200 hover:-translate-y-0.5 hover:scale-[1.01] hover:border-blue-200 hover:bg-blue-50/30 hover:shadow-[0_4px_12px_rgba(37,99,235,0.05)] dark:border-[#29466f] dark:bg-[#081a35] dark:hover:border-blue-500/50 dark:hover:bg-blue-950/10"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-black text-slate-900 dark:text-white truncate">{item.label}</p>
                      <div className="mt-1.5 flex items-center gap-2">
                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[9px] font-black uppercase tracking-wider ${getSectionBadgeStyle(item.section)}`}>
                          {item.section}
                        </span>
                      </div>
                    </div>
                    <span className="text-lg font-black text-slate-300 dark:text-slate-600">&rarr;</span>
                  </a>
                ))}
                {filteredSearchItems.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/50 px-4 py-8 text-center dark:border-[#29466f] dark:bg-[#081a35]/40">
                    <svg viewBox="0 0 24 24" className="mx-auto h-8 w-8 text-slate-300 dark:text-slate-600" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <circle cx="11" cy="11" r="7" />
                      <path d="m20 20-3.5-3.5" />
                    </svg>
                    <p className="mt-3 text-xs font-bold text-slate-700 dark:text-slate-200">No matches found</p>
                    <p className="mt-1 text-[10px] leading-5 text-slate-500 dark:text-slate-400">
                      Try a college name, city, branch, or exam module.
                    </p>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Breadcrumbs;
